import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleNettoPoints } from "@/utils/formatUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { tInterval } from "@/utils/translationUtils";
import { isDefined, numberOrZero } from "@/utils/utils";
import { Descriptions, Popover, Table, Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import { useFilterAgentCommissionsOutputs } from "../../../../../common/api/queries";
import type { UUID } from "../../../../../typings/global";
import { AGENT_ROUTE_PATHS } from "../../../../agent/paths";
import { useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import { PaidCommissionDetailTable } from "../../../batch/components/tables/PaidCommissionDetailTable";
import { CommissionsBatchStep } from "../../../batch/enums";
import { CommissionsOutput } from "../../../batch/types";
import { showPaidCommissionsPopover } from "../../../batch/utils";
import { CommissionBatchName } from "../../../components/CommissionBatchName";
import { OutputCalculatedCommissionModal } from "../modals/OutputCalculatedCommissionModal";
import { OutputSpecialCommissionModal } from "../modals/OutputSpecialCommissionModal";
import { PaidInBatchField } from "../PaidInBatchField";

interface Props {
  agentId: UUID;
  showFullBatchNameWithLink?: boolean;
  noActions?: boolean;
}

export const CommissionsOutputsAgentTable = ({ agentId, showFullBatchNameWithLink, noActions }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const searchParams = useFilterSearchParams();

  const { data: commissionsOutputs } = useFilterAgentCommissionsOutputs({ id: agentId, object: searchParams });

  const [outputToShowCalculatedCommissionsFor, setOutputToShowCalculatedCommissionsFor] = useState<CommissionsOutput>();

  const [outputToShowSpecialCommissionsFor, setOutputToShowSpecialCommissionsFor] = useState<CommissionsOutput>();

  const columns: ColumnsType<CommissionsOutput> = useMemo(
    () => [
      {
        key: "commissionsBatch",
        title: t("commissions.batch.attrs.commission.batch"),
        width: 240,
        ellipsis: { showTitle: false },
        render: (_, record) =>
          record.batch && (
            <CommissionBatchName
              batchName={record.batch.name}
              batchId={record.batch.id}
              showLink={showFullBatchNameWithLink}
            />
          ),
      },
      {
        key: "preliminaryOutputTag",
        width: 40,
        align: "center",
        render: (_, record) =>
          record.batch?.step === CommissionsBatchStep.REVIEW && (
            <Tooltip title={t("commissions.batch.helpers.preliminaryCommissionsOutput")}>
              <span>
                <AntIcon type="issues-close" />
              </span>
            </Tooltip>
          ),
      },
      {
        key: "calculatedCommissionAmount",
        title: t("commissions.batch.attrs.attachment.calculatedCommissionAmount"),
        align: "right",
        width: 135,
        render: (_, record) =>
          !record.batch?.stepChangeInProgress ? (
            <span>
              <ActionTextIcon
                text={formatLocaleCurrency(record.calculatedCommissionAmount)}
                onClick={() => setOutputToShowCalculatedCommissionsFor(record)}
                color="blue"
              />
            </span>
          ) : (
            formatLocaleCurrency(record.calculatedCommissionAmount)
          ),
      },
      {
        key: "nettoPointAmount",
        title: t("commissions.batch.helpers.nettoPointAmount"),
        align: "right",
        width: 135,
        render: (_, record) => (
          <Popover
            content={
              <Descriptions
                column={1}
                size="small"
                bordered
                styles={{ content: { textAlign: "right" } }}
                items={[
                  {
                    key: "individualPointAmount",
                    label: t("commissions.batch.attrs.attachment.individualPointAmount"),
                    children: formatLocaleNettoPoints(record.individualPointAmount, isTopAgentPlus ? 6 : 2),
                  },
                  {
                    key: "groupPointAmount",
                    label: t("commissions.batch.attrs.attachment.groupPointAmount"),
                    children: formatLocaleNettoPoints(record.groupPointAmount, isTopAgentPlus ? 6 : 2),
                  },
                  {
                    key: "totalPointAmount",
                    label: t("commissions.batch.attrs.attachment.totalPointAmount"),
                    children: formatLocaleNettoPoints(record.totalPointAmount, isTopAgentPlus ? 6 : 2),
                  },
                ]}
              />
            }
          >
            {formatLocaleNettoPoints(record.totalPointAmount)}
          </Popover>
        ),
      },
      {
        key: "specialCommissionAmount",
        title: t("commissions.batch.attrs.attachment.specialCommissionAmount"),
        align: "right",
        width: 135,
        render: (_, record) => (
          <Tooltip
            title={tInterval(
              "commissions.batch.helpers.specialCommissionCountTooltip_interval",
              record.specialCommissionCount,
            )}
          >
            {!record.batch?.stepChangeInProgress && record.specialCommissionCount > 0 ? (
              <span>
                <ActionTextIcon
                  text={formatLocaleCurrency(record.specialCommissionAmount)}
                  onClick={() => setOutputToShowSpecialCommissionsFor(record)}
                  color="blue"
                />
              </span>
            ) : (
              formatLocaleCurrency(record.specialCommissionAmount)
            )}
          </Tooltip>
        ),
      },
      {
        key: "bailAccount",
        title: t("commissions.batch.attrs.attachment.bailAccount"),
        children: [
          {
            key: "bailAccountOpeningBalance",
            title: t("commissions.batch.attrs.attachment.bailAccountOpeningBalance"),
            align: "right",
            width: 125,
            render: (_, record) =>
              isDefined(record.bailAccountOpeningBalance)
                ? formatLocaleCurrency(record.bailAccountOpeningBalance)
                : undefined,
          },
          {
            key: "bailAccountAutomaticMovement",
            title: t("commissions.batch.attrs.attachment.bailAccountAutomaticMovement"),
            align: "right",
            width: 125,
            render: (_, record) =>
              isDefined(record.bailAccountAutomaticMovement)
                ? formatLocaleCurrency(record.bailAccountAutomaticMovement)
                : undefined,
          },
          {
            key: "bailAccountManualMovement",
            title: t("commissions.batch.attrs.attachment.bailAccountManualMovement"),
            align: "right",
            width: 125,
            render: (_, record) =>
              isDefined(record.bailAccountManualMovement)
                ? formatLocaleCurrency(record.bailAccountManualMovement)
                : undefined,
          },
          {
            key: "bailAccountClosingBalance",
            title: t("commissions.batch.attrs.attachment.bailAccountClosingBalance"),
            align: "right",
            width: 125,
            render: (_, record) =>
              isDefined(record.bailAccountClosingBalance)
                ? formatLocaleCurrency(record.bailAccountClosingBalance)
                : undefined,
          },
        ],
      },
      {
        key: "debtAccount",
        title: t("commissions.batch.attrs.attachment.debtAccount"),
        children: [
          {
            key: "debtAccountOpeningBalance",
            title: t("commissions.batch.attrs.attachment.debtAccountOpeningBalance"),
            align: "right",
            width: 125,
            render: (_, record) => formatLocaleCurrency(record.debtAccountOpeningBalance),
          },
          {
            key: "debtAccountAutomaticMovement",
            title: t("commissions.batch.attrs.attachment.debtAccountAutomaticMovement"),
            align: "right",
            width: 125,
            render: (_, record) => formatLocaleCurrency(record.debtAccountAutomaticMovement),
          },
          {
            key: "debtAccountManualMovement",
            title: t("commissions.batch.attrs.attachment.debtAccountManualMovement"),
            align: "right",
            width: 125,
            render: (_, record) => formatLocaleCurrency(record.debtAccountManualMovement),
          },
          {
            key: "debtAccountClosingBalance",
            title: t("commissions.batch.attrs.attachment.debtAccountClosingBalance"),
            align: "right",
            width: 125,
            render: (_, record) => formatLocaleCurrency(record.debtAccountClosingBalance),
          },
        ],
      },
      {
        key: "paidCommissionAmount",
        title: t("commissions.batch.attrs.attachment.paidCommissionAmount"),
        align: "right",
        fixed: "right",
        width: 135,
        render: (_, record) => (
          <Popover
            content={showPaidCommissionsPopover(record) ? <PaidCommissionDetailTable data={record} /> : undefined}
          >
            <b>
              {showPaidCommissionsPopover(record)
                ? formatLocaleCurrency(
                    numberOrZero(record.calculatedCommissionAmountWithNoVat) +
                      numberOrZero(record.calculatedCommissionAmountWithoutVat),
                  )
                : formatLocaleCurrency(record.paidCommissionAmount)}
            </b>
          </Popover>
        ),
      },
      {
        key: "paidInBatch",
        align: "center",
        fixed: "right",
        width: 40,
        render: (_, record) => (
          <PaidInBatchField
            data={{
              paidInBatch: record.paidInBatch,
              id: record.id,
              optimisticLockVersion: record.optimisticLockVersion,
            }}
            noAction={!!noActions}
          />
        ),
      },
      {
        key: "actions",
        align: "right",
        fixed: "right",
        width: 90,
        render: (_, record) => (
          <ActionTextIcon
            path={generatePath(AGENT_ROUTE_PATHS.commissionsOutput.to, { id1: agentId, id2: record.id })}
            disabled={(record.batch?.step === CommissionsBatchStep.FINISH && !record.file) || !record.batch}
            target="_blank"
            icon="eye"
            color="blue"
            text={t("common.show")}
          />
        ),
      },
    ],
    [isTopAgentPlus],
  );

  return (
    <>
      <Table<CommissionsOutput>
        {...tableStandardProps()}
        columns={columns}
        scroll={{ x: 1950 }}
        dataSource={commissionsOutputs?.pageData}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(commissionsOutputs?.pageIndex) + 1,
          pageSize: commissionsOutputs?.pageSize,
          total: commissionsOutputs?.totalElementsCount,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
      />

      <OutputCalculatedCommissionModal
        isOpen={!!outputToShowCalculatedCommissionsFor}
        onClose={() => setOutputToShowCalculatedCommissionsFor(undefined)}
        agentId={agentId}
        commissionId={outputToShowCalculatedCommissionsFor?.id}
      />

      <OutputSpecialCommissionModal
        agentId={agentId}
        commissionsOutput={outputToShowSpecialCommissionsFor}
        onCancel={() => setOutputToShowSpecialCommissionsFor(undefined)}
      />
    </>
  );
};
